// src/views/Public/NfcProfile.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';

import { db } from '../../firebase';

import './NfcProfile.css';

const DEFAULT_HIGHLIGHTS = [
  {
    title: 'Respuesta inmediata',
    body: 'Atendemos tu solicitud en minutos para cerrar ventas mas rapido.',
  },
  {
    title: 'Experiencia premium',
    body: 'Presentacion moderna que transmite confianza desde el primer toque.',
  },
  {
    title: 'Todo en un lugar',
    body: 'Llamada, WhatsApp, mapa y contacto en un solo toque.',
  },
];

const DEFAULT_SERVICES = [
  'Ventas por WhatsApp y seguimiento instantaneo.',
  'Oferta clara con botones de contacto rapidos.',
  'Tarjeta digital lista para compartir.',
];

const buildVCard = ({ name, phone, email, address, website, businessName }) => {
  if (!name && !businessName && !phone && !email) return '';

  const displayName = businessName || name || 'Contacto';
  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${displayName}`,
  ];

  if (name) {
    lines.push(`N:${name};;;;`);
  }

  if (phone) {
    lines.push(`TEL;TYPE=CELL:${phone}`);
  }

  if (email) {
    lines.push(`EMAIL:${email}`);
  }

  if (address) {
    lines.push(`ADR;TYPE=WORK:;;${address};;;;`);
  }

  if (website) {
    lines.push(`URL:${website}`);
  }

  lines.push('END:VCARD');

  return lines.join('\n');
};

const sanitizeFileName = (value) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9-_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const NfcProfile = () => {
  const { clientId } = useParams();
  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState('loading');
  const [autoDownloadStatus, setAutoDownloadStatus] = useState('idle');
  const autoDownloadRef = useRef(false);

  useEffect(() => {
    const loadProfile = async () => {
      if (!clientId) {
        setStatus('error');
        return;
      }

      setStatus('loading');
      const candidateId = decodeURIComponent(clientId);

      try {
        let docRef = doc(db, 'clients', candidateId);
        let docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          const normalizedEmail = candidateId.includes('@')
            ? candidateId.toLowerCase()
            : null;
          const searches = [];

          if (normalizedEmail) {
            searches.push(where('email', '==', normalizedEmail));
          }

          searches.push(where('uid', '==', candidateId));

          let found = null;
          for (const condition of searches) {
            const qSnap = await getDocs(
              query(collection(db, 'clients'), condition, limit(1))
            );
            if (!qSnap.empty) {
              found = qSnap.docs[0];
              break;
            }
          }

          if (!found) {
            setStatus('not_found');
            return;
          }

          docSnap = found;
          docRef = doc(db, 'clients', found.id);
        }

        const data = docSnap.data();
        setProfile({ id: docSnap.id, ...data });
        setStatus('ready');
      } catch (err) {
        console.error('❌ Error cargando perfil NFC:', err);
        setStatus('error');
      }
    };

    loadProfile();
  }, [clientId]);

  const displayName = profile?.businessName || profile?.name || 'Tu negocio';
  const tagline =
    profile?.tagline ||
    'Presentacion movil para convertir visitas en clientes.';
  const description =
    profile?.description ||
    'Comparte tu oferta en segundos, genera confianza y lleva al cliente directo a comprar o reservar.';
  const category =
    profile?.category || profile?.industry || 'Tarjeta NFC inteligente';
  const phoneRaw = profile?.phone || profile?.phoneNumber || '';
  const phoneDigits = phoneRaw.replace(/[^\d]/g, '');
  const email = profile?.email || profile?.contactEmail || '';
  const address = profile?.address || profile?.location || '';
  const website = profile?.website || profile?.site || '';
  const avatar = profile?.profileImageUrl || '/logo-bbts.png';
  const highlights =
    Array.isArray(profile?.highlights) && profile.highlights.length
      ? profile.highlights
      : DEFAULT_HIGHLIGHTS;
  const services =
    Array.isArray(profile?.services) && profile.services.length
      ? profile.services
      : DEFAULT_SERVICES;
  const whatsappLink = phoneDigits ? `https://wa.me/${phoneDigits}` : '';
  const telLink = phoneDigits ? `tel:${phoneDigits}` : '';
  const mailLink = email ? `mailto:${email}` : '';
  const mapLink = address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        address
      )}`
    : '';

  const vcardPayload = useMemo(
    () =>
      buildVCard({
        name: profile?.name || '',
        businessName: profile?.businessName || '',
        phone: phoneDigits,
        email,
        address,
        website,
      }),
    [profile, phoneDigits, email, address, website]
  );

  const triggerDownload = () => {
    if (!vcardPayload) return;

    try {
      const blob = new Blob([vcardPayload], { type: 'text/vcard' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      const fallbackName = displayName || 'contacto';
      const fileName = sanitizeFileName(fallbackName) || 'contacto';

      anchor.href = url;
      anchor.download = `${fileName}.vcf`;
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      setTimeout(() => URL.revokeObjectURL(url), 500);
      setAutoDownloadStatus('done');
    } catch (err) {
      console.error('❌ Error descargando contacto:', err);
      setAutoDownloadStatus('failed');
    }
  };

  useEffect(() => {
    if (!profile || autoDownloadRef.current || !vcardPayload) return;
    autoDownloadRef.current = true;
    setAutoDownloadStatus('starting');

    const timer = setTimeout(() => {
      triggerDownload();
    }, 700);

    return () => clearTimeout(timer);
  }, [profile, vcardPayload]);

  if (status === 'loading') {
    return (
      <div className="nfc-profile-page">
        <div className="nfc-profile-shell nfc-status">
          <p>Preparando tu tarjeta digital...</p>
        </div>
      </div>
    );
  }

  if (status === 'not_found') {
    return (
      <div className="nfc-profile-page">
        <div className="nfc-profile-shell nfc-status">
          <h2>Tarjeta no encontrada</h2>
          <p>Verifica el enlace NFC o solicita uno nuevo.</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="nfc-profile-page">
        <div className="nfc-profile-shell nfc-status">
          <h2>Algo salio mal</h2>
          <p>Intenta nuevamente en unos minutos.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="nfc-profile-page">
      <div className="nfc-profile-shell">
        <header className="nfc-hero">
          <div className="nfc-hero__media">
            <img src={avatar} alt={displayName} className="nfc-hero__avatar" />
          </div>
          <div className="nfc-hero__content">
            <p className="nfc-kicker">{category}</p>
            <h1>{displayName}</h1>
            <p className="nfc-tagline">{tagline}</p>
            <p className="nfc-description">{description}</p>
            <div className="nfc-actions">
              {telLink && (
                <a className="nfc-btn nfc-btn--primary" href={telLink}>
                  Llamar ahora
                </a>
              )}
              {whatsappLink && (
                <a
                  className="nfc-btn nfc-btn--ghost"
                  href={whatsappLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  WhatsApp directo
                </a>
              )}
            </div>
            <div className="nfc-contact-row">
              {mailLink && (
                <a className="nfc-link" href={mailLink}>
                  {email}
                </a>
              )}
              {mapLink && (
                <a
                  className="nfc-link"
                  href={mapLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  Abrir ubicacion
                </a>
              )}
              {website && (
                <a
                  className="nfc-link"
                  href={website}
                  target="_blank"
                  rel="noreferrer"
                >
                  {website.replace(/^https?:\/\//, '')}
                </a>
              )}
            </div>
          </div>
        </header>

        <section className="nfc-cta-card">
          <div>
            <h2>Contacto instantaneo</h2>
            <p>
              Al tocar la tarjeta, tu contacto se descarga automaticamente para
              que no pierdas ninguna venta.
            </p>
            {autoDownloadStatus === 'starting' && (
              <p className="nfc-note">Descargando contacto...</p>
            )}
            {autoDownloadStatus === 'done' && (
              <p className="nfc-note">Contacto descargado con exito.</p>
            )}
            {autoDownloadStatus === 'failed' && (
              <p className="nfc-note">
                Si no se descargo, toca el boton para guardar el contacto.
              </p>
            )}
          </div>
          <button
            type="button"
            className="nfc-btn nfc-btn--dark"
            onClick={triggerDownload}
          >
            Guardar contacto
          </button>
        </section>

        <section className="nfc-section">
          <h2>Por que esta tarjeta vende mas</h2>
          <div className="nfc-grid">
            {highlights.map((item, index) => {
              const title = typeof item === 'string' ? item : item.title;
              const body = typeof item === 'string' ? '' : item.body;
              return (
                <article key={index} className="nfc-card">
                  <h3>{title || `Beneficio ${index + 1}`}</h3>
                  <p>{body || 'Mensaje breve para destacar tu oferta.'}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="nfc-section nfc-section--light">
          <h2>Lo que recibe tu cliente</h2>
          <ul className="nfc-list">
            {services.map((service, index) => {
              const label =
                typeof service === 'string'
                  ? service
                  : service.label || service.title || 'Servicio destacado';
              return <li key={index}>{label}</li>;
            })}
          </ul>
        </section>

        <section className="nfc-bottom-card">
          <div>
            <h3>Listo para vender ahora</h3>
            <p>
              Comparte esta tarjeta y convierte visitas en oportunidades reales.
            </p>
          </div>
          <div className="nfc-actions">
            {whatsappLink && (
              <a
                className="nfc-btn nfc-btn--primary"
                href={whatsappLink}
                target="_blank"
                rel="noreferrer"
              >
                Cotizar por WhatsApp
              </a>
            )}
            {telLink && (
              <a className="nfc-btn nfc-btn--ghost" href={telLink}>
                Llamar
              </a>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default NfcProfile;
